import { useEffect, useState } from 'react';

const DISMISS_KEY = 'bw-install-hint-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Compact install banner. Two cases:
 *  - iOS Safari (no programmatic install) → text instructions for Add to Home Screen.
 *  - Chrome / Edge / Android with a captured beforeinstallprompt → an Install button.
 *
 * Hidden when running standalone (already installed) or when the user has dismissed it.
 */
export function InstallHint(): JSX.Element | null {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  });
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState<boolean>(() => isInStandaloneMode());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setIsStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt as EventListener);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt as EventListener);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (dismissed || isStandalone) return null;

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const isIosSafari = detectIosSafari();
  if (!installEvent && !isIosSafari) return null;

  const triggerInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      dismiss();
    }
    setInstallEvent(null);
  };

  return (
    <div
      role="region"
      aria-label="Install Bridgewerk"
      className="mx-auto max-w-[1400px] mt-2 mb-3 px-3 sm:px-5 flex items-center justify-between gap-3 rounded-md border border-border bg-card/60 backdrop-blur-sm"
      style={{ minHeight: 44 }}
    >
      <p className="text-xs text-text-dim font-mono leading-snug py-2">
        {installEvent ? (
          <>Install Bridgewerk for full-screen, faster launches.</>
        ) : (
          <>
            Install: tap <span className="text-accent">Share</span>{' '}
            then <span className="text-accent">&ldquo;Add to Home Screen&rdquo;</span>.
          </>
        )}
      </p>
      <div className="flex items-center gap-1 shrink-0">
        {installEvent && (
          <button
            type="button"
            onClick={() => void triggerInstall()}
            className="touch-target px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] text-accent hover:text-text"
          >
            Install
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install hint"
          className="touch-target px-3 py-2 text-text-dim hover:text-text"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function isInStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari historically used a non-standard navigator.standalone.
  return Boolean((window.navigator as unknown as { standalone?: boolean }).standalone);
}

function detectIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  if (!isIos) return false;
  // Excludes in-app browsers (Chrome on iOS uses CriOS, Firefox uses FxiOS, etc.)
  // — those can't trigger the share-sheet install flow either.
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}
